/**
 * `aethereum mcp` — a pure-MCP, no-tmux entry point so Aethereum works with NATIVE Claude
 * Code (and any MCP client). Register it with:
 *
 *   claude mcp add aethereum -- npx -y aethereum mcp --join "<share-url>" --name you
 *
 * It joins a room (the share URL from `aethereum --share`), then exposes the three
 * Aethereum tools over stdio. This is the pull-based path (no live tmux push); the
 * `aethereum run claude` wrapper adds live collision pushes on top.
 */
import { HttpHiveClient } from "./client.js";
import { outboxForToken } from "./write-outbox.js";
import { registerHiveTools } from "./mcp.js";

export interface McpStdioOptions {
  /** A room share URL: `<serverUrl>?token=<shareToken>` (from `aethereum --share`). */
  join?: string;
  /** Alternatively, an already-joined server URL + session token. */
  server?: string;
  token?: string;
  name?: string;
}

interface JoinResult {
  serverUrl: string;
  sessionToken: string;
}

/** Join a room via its share URL and return the server URL + a session token. */
export async function joinViaShareUrl(shareUrl: string, name: string): Promise<JoinResult> {
  const url = new URL(shareUrl);
  const shareToken = url.searchParams.get("token");
  if (!shareToken) throw new Error("share URL is missing its ?token=… parameter");
  const serverUrl = `${url.protocol}//${url.host}`;

  const res = await fetch(`${serverUrl}/join`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: shareToken, name, type: "agent" }),
  });
  if (!res.ok) throw new Error(`join failed: ${res.status}`);
  const data = (await res.json()) as { sessionToken: string };
  return { serverUrl, sessionToken: data.sessionToken };
}

/** Build a Aethereum MCP server bound to a room server URL + session token. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createHiveMcpServer(serverUrl: string, sessionToken: string): Promise<any> {
  const { McpServer } = await import("@modelcontextprotocol/sdk/server/mcp.js");
  const server = new McpServer({ name: "aethereum", version: "0.1.0" });
  const client = new HttpHiveClient(
    serverUrl.replace(/\/$/, ""),
    sessionToken,
    fetch,
    outboxForToken(sessionToken),
  );
  registerHiveTools(server, () => client);
  return server;
}

export async function runMcpStdio(opts: McpStdioOptions): Promise<void> {
  const name = opts.name ?? "agent";

  let serverUrl: string;
  let sessionToken: string;
  if (opts.join) {
    ({ serverUrl, sessionToken } = await joinViaShareUrl(opts.join, name));
  } else if (opts.server && opts.token) {
    serverUrl = opts.server;
    sessionToken = opts.token;
  } else {
    throw new Error("provide --join <share-url> (or --server <url> --token <session-token>)");
  }

  const server = await createHiveMcpServer(serverUrl, sessionToken);
  const { StdioServerTransport } = await import("@modelcontextprotocol/sdk/server/stdio.js");
  await server.connect(new StdioServerTransport());
  // Stays alive until stdin closes (Claude Code manages the lifecycle).
}
